import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CaretLeft,
  CaretRight,
  Sparkle,
  Brain,
  Stethoscope,
  CloudArrowUp,
  Code,
} from "@phosphor-icons/react";
import { optimizeImageUrl } from "../lib/image-url";

const SLIDES = [
  {
    id: "transformation",
    eyebrow: "Digital Transformation Partner",
    icon: Sparkle,
    title: ["We engineer the", "next-gen of IT."],
    italicWord: "next-gen",
    description:
      "Senior-led teams building web, mobile, AI, cloud, and healthcare-IT for ambitious companies across 18 countries.",
    primaryCta: { label: "Book a Free Consultation", to: "/contact" },
    secondaryCta: { label: "Explore Services", to: "/services" },
    image: optimizeImageUrl(
      "https://images.unsplash.com/photo-1497366216548-37526070297c",
      { width: 1024, quality: 68 },
    ),
    tag: "Est. 2013 · 240+ clients",
  },
  {
    id: "ai",
    eyebrow: "Artificial Intelligence",
    icon: Brain,
    title: ["AI that ships.", "Not AI that demos."],
    italicWord: "ships",
    description:
      "Production-grade LLM agents, RAG pipelines, and computer vision — deployed, observed, and actually used by your teams.",
    primaryCta: { label: "Explore AI Services", to: "/services/artificial-intelligence" },
    secondaryCta: { label: "Read AI Case Study", to: "/services/artificial-intelligence#process" },
    image: optimizeImageUrl(
      "https://images.unsplash.com/photo-1677442136019-21780ecad995",
      { width: 1024, quality: 68 },
    ),
    tag: "94% accuracy · 8-week MVPs",
  },
  {
    id: "healthcare",
    eyebrow: "Healthcare IT",
    icon: Stethoscope,
    title: ["HIPAA-compliant coding,", "billing & tech."],
    italicWord: "HIPAA-compliant",
    description:
      "AAPC-certified coders, 98%+ accuracy, 24-hour turnaround, and modern RCM — serving hospitals, clinics, and RCM companies across the US.",
    primaryCta: { label: "See Healthcare IT", to: "/services/medical-coding" },
    secondaryCta: { label: "Medical Billing", to: "/services/medical-billing" },
    image: optimizeImageUrl(
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d",
      { width: 1024, quality: 68 },
    ),
    tag: "SOC 2 Type II · 24hr TAT",
  },
  {
    id: "cloud",
    eyebrow: "Cloud & DevOps",
    icon: CloudArrowUp,
    title: ["Scale globally.", "Pay for what you use."],
    italicWord: "globally",
    description:
      "AWS, Azure, and GCP architecture, migration, Kubernetes orchestration, and 24/7 SRE coverage — with FinOps built in.",
    primaryCta: { label: "Explore Cloud Services", to: "/services/cloud" },
    secondaryCta: { label: "See Case Study", to: "/services/cloud#process" },
    image: optimizeImageUrl(
      "https://images.unsplash.com/photo-1544197150-b99a580bb7a8",
      { width: 1024, quality: 68 },
    ),
    tag: "30–50% cost reduction",
  },
  {
    id: "engineering",
    eyebrow: "Product Engineering",
    icon: Code,
    title: ["From idea to", "launched product."],
    italicWord: "launched",
    description:
      "Discovery, design, full-stack engineering, and GTM — in one senior squad. The team you'd hire if you could find them.",
    primaryCta: { label: "Discuss Your Product", to: "/contact" },
    secondaryCta: { label: "See Our Process", to: "/services/product-development#process" },
    image: optimizeImageUrl(
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c",
      { width: 1024, quality: 68 },
    ),
    tag: "0 → $1M ARR in 9 months",
  },
];

const AUTO_ADVANCE_MS = 6500;

export default function HomeCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [index, paused]);

  function goTo(i) {
    setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
  }

  const slide = SLIDES[index];
  const IconCmp = slide.icon;

  // Render title with italic accent word
  function renderTitle() {
    return slide.title.map((line, li) => (
      <span key={li} className="block">
        {line.split(" ").map((w, wi) => {
          const clean = w.replace(/[.,]/g, "");
          const isAccent = clean.toLowerCase() === slide.italicWord.toLowerCase();
          return (
            <span key={wi}>
              {isAccent ? (
                <span className="relative inline-block text-brand-mint italic font-normal text-mint-glow">
                  {w}
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    height="10"
                    viewBox="0 0 200 10"
                    fill="none"
                    preserveAspectRatio="none"
                  >
                    <path d="M2 7C50 3 100 2 198 6" stroke="#5FD9C4" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              ) : (
                <span>{w}</span>
              )}{" "}
            </span>
          );
        })}
      </span>
    ));
  }

  return (
    <section
      data-testid="home-carousel"
      className="relative w-full overflow-hidden pt-24 lg:pt-28"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[620px] sm:h-[680px] lg:h-[780px] w-full">
        {/* Slide layers */}
        <AnimatePresence mode="sync">
          <motion.div
            key={slide.id}
            initial={index === 0 ? false : { opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: index === 0 ? 0.35 : 0.75, ease: [0.65, 0, 0.35, 1] }}
            className="absolute inset-0"
          >
            <img
              src={slide.image}
              alt={slide.eyebrow}
              className="w-full h-full object-cover"
              width={1280}
              height={720}
              decoding="async"
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "auto"}
            />
            {/* Dark overlay + brand gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy-darker via-brand-navy-darker/85 to-brand-navy-darker/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-darker via-transparent to-transparent" />
            <div className="absolute inset-0 bg-blueprint opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-br from-brand-mint/[0.06] via-transparent to-transparent mix-blend-overlay" />
          </motion.div>
        </AnimatePresence>

        {/* Content */}
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id + "-content"}
              initial={index === 0 ? false : { opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: index === 0 ? 0.35 : 0.55, ease: "easeOut" }}
              className="max-w-3xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-brand-mint/30 bg-brand-mint/10 backdrop-blur-sm px-4 py-1.5 mb-7">
                <IconCmp size={14} weight="duotone" className="text-brand-mint" />
                <span className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-brand-mint">
                  {slide.eyebrow}
                </span>
              </div>

              <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tighter text-white">
                {renderTitle()}
              </h1>

              <p className="mt-7 max-w-2xl text-base sm:text-lg lg:text-xl text-white/80 leading-relaxed">
                {slide.description}
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to={slide.primaryCta.to}
                  data-testid={`carousel-cta-primary-${slide.id}`}
                  className="group inline-flex items-center gap-2 bg-brand-mint text-brand-navy-darker hover:bg-brand-mint-light transition rounded-full px-6 sm:px-7 py-3.5 sm:py-4 font-semibold text-sm sm:text-base mint-glow"
                >
                  {slide.primaryCta.label}
                  <ArrowRight
                    size={18}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
                <Link
                  to={slide.secondaryCta.to}
                  data-testid={`carousel-cta-secondary-${slide.id}`}
                  className="inline-flex items-center gap-2 border border-white/25 hover:border-brand-mint/60 hover:text-brand-mint bg-brand-navy-darker/20 backdrop-blur-sm transition rounded-full px-6 sm:px-7 py-3.5 sm:py-4 text-white text-sm sm:text-base"
                >
                  {slide.secondaryCta.label}
                </Link>
              </div>

              <div className="mt-8 flex items-center gap-3">
                <span className="w-8 h-px bg-brand-mint" />
                <span className="font-mono-accent text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-white/75">
                  {slide.tag}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom controls bar */}
        <div className="absolute left-0 right-0 bottom-5 sm:bottom-8 z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              {/* Slide indicator + progress */}
              <div className="flex items-center gap-4">
                <div className="font-mono-accent text-[10px] sm:text-xs text-white/70 tabular-nums tracking-[0.2em]">
                  <span className="text-brand-mint">{String(index + 1).padStart(2, "0")}</span>
                  <span className="mx-1.5 text-white/30">/</span>
                  <span>{String(SLIDES.length).padStart(2, "0")}</span>
                </div>
                <div className="flex items-center gap-2">
                  {SLIDES.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => goTo(i)}
                      data-testid={`carousel-dot-${i}`}
                      aria-label={`Go to slide ${i + 1}: ${s.eyebrow}`}
                      className="group relative h-1 rounded-full overflow-hidden bg-white/20 hover:bg-white/40 transition-all"
                      style={{ width: i === index ? "44px" : "20px" }}
                    >
                      {i === index && !paused && (
                        <motion.span
                          key={s.id}
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: "linear" }}
                          className="absolute inset-y-0 left-0 bg-brand-mint"
                        />
                      )}
                      {i === index && paused && (
                        <span className="absolute inset-0 bg-brand-mint" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prev / Next buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goTo(index - 1)}
                  data-testid="carousel-prev"
                  aria-label="Previous slide"
                  className="rounded-full border border-white/20 hover:border-brand-mint hover:bg-brand-mint hover:text-brand-navy-darker text-white p-2.5 sm:p-3 backdrop-blur-sm transition"
                >
                  <CaretLeft size={16} weight="bold" />
                </button>
                <button
                  onClick={() => goTo(index + 1)}
                  data-testid="carousel-next"
                  aria-label="Next slide"
                  className="rounded-full border border-white/20 hover:border-brand-mint hover:bg-brand-mint hover:text-brand-navy-darker text-white p-2.5 sm:p-3 backdrop-blur-sm transition"
                >
                  <CaretRight size={16} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted-by strip */}
        <div className="absolute top-28 right-6 lg:right-8 hidden xl:block z-10">
          <div className="bg-brand-navy-darker/50 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 w-[240px]">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-brand-mint mb-3">
              Trusted globally
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-white/70 font-display text-sm">
              <span>Medicare+</span>
              <span className="text-brand-mint/60">·</span>
              <span>LoopLogistics</span>
              <span className="text-brand-mint/60">·</span>
              <span>FinServe</span>
              <span className="text-brand-mint/60">·</span>
              <span>BrightAI</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
